import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { timeApi } from '../../../api/time.js';
import { pmApi } from '../../../api/pm.js';

/**
 * Timesheet detail — a WEEKLY GRID of day cells across the timesheet's period.
 * Each day shows its posted entries + a quick-add row (hours, project/issue
 * picker, cost center, billable). A running total reflects the timesheet's
 * recomputed total_hours. Drafts can be submitted for approval; non-drafts are
 * read-only.
 */

/**
 * A stable calendar-day key (YYYY-MM-DD) read from LOCAL date components. The API
 * serialises DATE columns as a timestamp at the server's local midnight; reading
 * it back with UTC slicing would shift the day (e.g. -1 in +TZ regions), pushing
 * posted entries out of the rendered cell range. Using local components keeps the
 * grid cells and the entry dates aligned (the browser shares the server TZ in our
 * deployment) so entries always render under their own day.
 */
function dayKey(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function daysInPeriod(start, end) {
  const out = [];
  if (!start || !end) return out;
  const s = new Date(`${dayKey(start)}T00:00:00`); // local midnight
  const e = new Date(`${dayKey(end)}T00:00:00`);
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    out.push(dayKey(d));
  }
  return out;
}

export default function TimesheetDetailPage() {
  const { id } = useParams();
  const [timesheet, setTimesheet] = useState(null);
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  function refresh() {
    setLoading(true);
    timeApi
      .getTimesheet(id)
      .then((d) => {
        setTimesheet(d?.timesheet || null);
        setEntries(d?.entries || []);
      })
      .catch((e) => setError(e.response?.data?.message || 'Failed to load timesheet'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, [id]);

  useEffect(() => {
    pmApi.projects().then((r) => setProjects(r?.items || r || [])).catch(() => setProjects([]));
    pmApi.issues().then((r) => setIssues(r?.items || r || [])).catch(() => setIssues([]));
  }, []);

  const days = useMemo(
    () => daysInPeriod(timesheet?.period_start, timesheet?.period_end),
    [timesheet?.period_start, timesheet?.period_end]
  );
  const entriesByDay = useMemo(() => {
    const map = {};
    for (const e of entries) {
      const key = dayKey(e.date);
      (map[key] = map[key] || []).push(e);
    }
    return map;
  }, [entries]);

  const isDraft = timesheet?.status === 'draft';
  const total = Number(timesheet?.total_hours || 0);

  async function addEntry(date, e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await timeApi.addEntry(id, {
        date,
        hours: Number(fd.get('hours')),
        projectId: fd.get('projectId') || null,
        issueId: fd.get('issueId') || null,
        costCenter: fd.get('costCenter') || null,
        billable: fd.get('billable') === 'on',
        notes: fd.get('notes') || null,
      });
      e.target.reset();
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add entry');
    } finally {
      setBusy(false);
    }
  }

  async function removeEntry(entryId) {
    setBusy(true);
    try {
      await timeApi.removeEntry(entryId);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove entry');
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await timeApi.submitTimesheet(id);
      setNotice('Submitted for approval');
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="time-timesheet-detail-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Timesheet</h1>
          <p className="muted">
            {timesheet ? `${timesheet.employee_name || timesheet.employee_code || ''} · ${String(timesheet.period_start).slice(0, 10)} → ${String(timesheet.period_end).slice(0, 10)}` : '—'}
          </p>
        </div>
        <Link className="btn ghost" to="/time/timesheets">← Timesheets</Link>
      </header>

      {error && <div className="alert error" data-testid="time-timesheet-detail-error">{error}</div>}
      {notice && <div className="alert info" data-testid="time-timesheet-detail-notice">{notice}</div>}

      <div className="card">
        <div className="page-header">
          <div>
            <strong>Status: <span data-testid="time-timesheet-status">{timesheet?.status || '—'}</span></strong>
          </div>
          <div>
            <span className="badge">Total: <span data-testid="time-timesheet-total">{total}</span> h</span>
            {isDraft && (
              <button className="btn primary" data-testid="time-timesheet-submit" onClick={submit} disabled={busy}>
                Submit for approval
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card-grid" data-testid="time-timesheet-grid" data-loading={loading ? 'true' : 'false'}>
        {days.map((date) => {
          const dayEntries = entriesByDay[date] || [];
          const dayTotal = dayEntries.reduce((s, en) => s + Number(en.hours || 0), 0);
          return (
            <div key={date} className="card mini" data-testid={`time-entry-${date}`}>
              <h3>{date} <span className="muted">({dayTotal}h)</span></h3>
              <ul>
                {dayEntries.map((en) => (
                  <li key={en.id} data-testid={`time-entry-row-${en.id}`}>
                    {Number(en.hours)}h · {en.project_name || 'no project'}{en.issue_title ? ` / ${en.issue_title}` : ''}
                    {en.billable ? ' · billable' : ''}
                    {isDraft && (
                      <button className="btn ghost" data-testid={`time-entry-remove-${en.id}`} onClick={() => removeEntry(en.id)} disabled={busy}>×</button>
                    )}
                  </li>
                ))}
                {dayEntries.length === 0 && <li className="muted">No entries.</li>}
              </ul>
              {isDraft && (
                <form onSubmit={(e) => addEntry(date, e)} className="inline-form" data-testid={`time-entry-form-${date}`}>
                  <input name="hours" type="number" min="0" max="24" step="0.25" placeholder="hrs" data-testid={`time-entry-hours-${date}`} required />
                  <select name="projectId" defaultValue="" data-testid={`time-entry-project-${date}`}>
                    <option value="">— project —</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <select name="issueId" defaultValue="" data-testid={`time-entry-issue-${date}`}>
                    <option value="">— issue —</option>
                    {issues.map((i) => <option key={i.id} value={i.id}>{i.title || i.key}</option>)}
                  </select>
                  <input name="costCenter" placeholder="cost center" data-testid={`time-entry-costcenter-${date}`} />
                  <label className="check"><input name="billable" type="checkbox" data-testid={`time-entry-billable-${date}`} /> billable</label>
                  <input name="notes" placeholder="notes" data-testid={`time-entry-notes-${date}`} />
                  <button className="btn primary" type="submit" data-testid={`time-entry-add-${date}`} disabled={busy}>Add</button>
                </form>
              )}
            </div>
          );
        })}
        {!loading && days.length === 0 && <p className="muted">No period range.</p>}
      </div>
    </section>
  );
}
